
<template>
  <section class="calculator-module">
    <div class="module-header">
      <h2>{{ t('tankLog.title').value }}</h2>
      <span class="toggle-icon">▼</span>
    </div>
    <div class="module-content">
      <div class="calculator-intro">
        <h3>{{ t('tankLog.subtitle').value }}</h3>
        <p>{{ t('tankLog.description').value }}</p>
      </div>
      
      <div class="calculator-tabs">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'water' }"
          @click="activeTab = 'water'"
        >
          {{ t('tankLog.tabs.water').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'maintenance' }"
          @click="activeTab = 'maintenance'"
        >
          {{ t('tankLog.tabs.maintenance').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'livestock' }"
          @click="activeTab = 'livestock'"
        >
          {{ t('tankLog.tabs.livestock').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'history' }"
          @click="activeTab = 'history'"
        >
          {{ t('tankLog.tabs.history').value }}
        </button>
      </div>
      
      <!-- 水质记录 -->
      <div v-if="activeTab === 'water'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('tankLog.sections.waterParams').value }}</h4>
          <div class="form-row">
            <div class="form-group">
              <label for="temperature">{{ t('tankLog.labels.temperature').value }}</label>
              <input
                type="number"
                id="temperature"
                v-model="waterParams.temperature"
                class="form-control"
                :placeholder="t('tankLog.placeholders.temperature').value"
                step="0.1"
                min="15"
                max="35"
              >
            </div>
            <div class="form-group">
              <label for="salinity">{{ t('tankLog.labels.salinity').value }}</label>
              <input
                type="number"
                id="salinity"
                v-model="waterParams.salinity"
                class="form-control"
                :placeholder="t('tankLog.placeholders.salinity').value"
                step="0.001"
                min="1.015"
                max="1.040"
              >
              <div v-if="waterParams.salinity" class="salinity-conversion">
                {{ sgToSalinity(parseFloat(waterParams.salinity.toString())) }} ppt
              </div>
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="ph">{{ t('tankLog.labels.ph').value }}</label>
              <input
                type="number"
                id="ph"
                v-model="waterParams.ph"
                class="form-control"
                :placeholder="t('tankLog.placeholders.ph').value"
                step="0.1"
                min="7.5"
                max="8.5"
              >
            </div>
            <div class="form-group">
              <label for="alkalinity">{{ t('tankLog.labels.alkalinity').value }}</label>
              <input
                type="number"
                id="alkalinity"
                v-model="waterParams.alkalinity"
                class="form-control"
                :placeholder="t('tankLog.placeholders.alkalinity').value"
                step="0.1"
                min="6"
                max="12"
              >
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="calcium">{{ t('tankLog.labels.calcium').value }}</label>
              <input
                type="number"
                id="calcium"
                v-model="waterParams.calcium"
                class="form-control"
                :placeholder="t('tankLog.placeholders.calcium').value"
                step="1"
                min="350"
                max="500"
              >
            </div>
            <div class="form-group">
              <label for="magnesium">{{ t('tankLog.labels.magnesium').value }}</label>
              <input
                type="number"
                id="magnesium"
                v-model="waterParams.magnesium"
                class="form-control"
                :placeholder="t('tankLog.placeholders.magnesium').value"
                step="1"
                min="1200"
                max="1500"
              >
            </div>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="nitrate">{{ t('tankLog.labels.nitrate').value }}</label>
              <input
                type="number"
                id="nitrate"
                v-model="waterParams.nitrate"
                class="form-control"
                :placeholder="t('tankLog.placeholders.nitrate').value"
                step="0.1"
                min="0"
                max="50"
              >
            </div>
            <div class="form-group">
              <label for="phosphate">{{ t('tankLog.labels.phosphate').value }}</label>
              <input
                type="number"
                id="phosphate"
                v-model="waterParams.phosphate"
                class="form-control"
                :placeholder="t('tankLog.placeholders.phosphate').value"
                step="0.01"
                min="0"
                max="1"
              >
            </div>
          </div>
          
          <div class="form-group">
            <label for="water_notes">{{ t('tankLog.labels.notes').value }}</label>
            <textarea
              id="water_notes"
              v-model="waterParams.notes"
              class="form-control textarea"
              :placeholder="t('tankLog.placeholders.waterNotes').value"
              rows="3"
            ></textarea>
          </div>

          <!-- 水质拍照功能 -->
          <div class="photo-section">
            <h5>{{ t('tankLog.results.photoSection.waterPhoto').value }}</h5>
            <CameraCapture @photoCaptured="handleWaterPhotoCaptured" />
            
            <!-- 照片预览 -->
            <div v-if="waterParams.photos.length > 0" class="photo-previews">
              <h6>{{ t('tankLog.results.photoSection.photosTaken').value }}</h6>
              <div class="preview-grid">
                <div
                  v-for="(photo, index) in waterParams.photos"
                  :key="index"
                  class="photo-preview-item"
                >
                  <img :src="photo" alt="水质照片" class="preview-thumbnail" />
                  <button class="remove-photo-btn" @click="removeWaterPhoto(index)">
                    ×
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="saveWaterRecord"
          :disabled="!canSaveWaterRecord"
        >
          {{ t('tankLog.buttons.saveWaterRecord').value }}
        </button>
      </div>
      
      <!-- 维护记录 -->
      <div v-if="activeTab === 'maintenance'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('tankLog.sections.maintenanceRecord').value }}</h4>
          
          <div class="form-group">
            <label for="maintenance_type">{{ t('tankLog.labels.maintenanceType').value }}</label>
            <select
              id="maintenance_type"
              v-model="maintenanceRecord.type"
              class="select-control"
            >
              <option value="water_change">{{ t('tankLog.options.maintenanceType.waterChange').value }}</option>
              <option value="filter_cleaning">{{ t('tankLog.options.maintenanceType.filterCleaning').value }}</option>
              <option value="skimmer_cleaning">{{ t('tankLog.options.maintenanceType.skimmerCleaning').value }}</option>
              <option value="glass_cleaning">{{ t('tankLog.options.maintenanceType.glassCleaning').value }}</option>
              <option value="equipment_check">{{ t('tankLog.options.maintenanceType.equipmentCheck').value }}</option>
              <option value="other">{{ t('tankLog.options.maintenanceType.other').value }}</option>
            </select>
          </div>
          
          <div class="form-group" v-if="maintenanceRecord.type === 'water_change'">
            <label for="water_change_amount">{{ t('tankLog.labels.waterChangeAmount').value }}</label>
            <input
              type="number"
              id="water_change_amount"
              v-model="maintenanceRecord.waterChangeAmount"
              class="form-control"
              :placeholder="t('tankLog.placeholders.waterChangeAmount').value"
              step="0.1"
              min="1"
            >
          </div>
          
          <div class="form-group">
            <label for="maintenance_duration">{{ t('tankLog.labels.duration').value }}</label>
            <input
              type="number"
              id="maintenance_duration"
              v-model="maintenanceRecord.duration"
              class="form-control"
              :placeholder="t('tankLog.placeholders.duration').value"
              step="1"
              min="1"
              max="480"
            >
          </div>
          
          <div class="form-group">
            <label for="maintenance_notes">{{ t('tankLog.labels.maintenanceDetails').value }}</label>
            <textarea
              id="maintenance_notes"
              v-model="maintenanceRecord.notes"
              class="form-control textarea"
              :placeholder="t('tankLog.placeholders.maintenanceDetails').value"
              rows="4"
            ></textarea>
          </div>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="maintenanceRecord.completed"
              >
              <span class="checkbox-custom"></span>
              {{ t('tankLog.labels.completed').value }}
            </label>
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="saveMaintenanceRecord"
          :disabled="!canSaveMaintenanceRecord"
        >
          {{ t('tankLog.buttons.saveMaintenanceRecord').value }}
        </button>
      </div>
      
      <!-- 生物状态 -->
      <div v-if="activeTab === 'livestock'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('tankLog.sections.livestockRecord').value }}</h4>
          
          <div class="form-group">
            <label for="livestock_type">{{ t('tankLog.labels.livestockType').value }}</label>
            <select
              id="livestock_type"
              v-model="livestockRecord.type"
              class="select-control"
            >
              <option value="fish">{{ t('tankLog.options.livestockType.fish').value }}</option>
              <option value="coral">{{ t('tankLog.options.livestockType.coral').value }}</option>
              <option value="invertebrate">{{ t('tankLog.options.livestockType.invertebrate').value }}</option>
              <option value="other">{{ t('tankLog.options.livestockType.other').value }}</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="livestock_name">{{ t('tankLog.labels.livestockName').value }}</label>
            <input
              type="text"
              id="livestock_name"
              v-model="livestockRecord.name"
              class="form-control"
              :placeholder="t('tankLog.placeholders.livestockName').value"
            >
          </div>
          
          <div class="form-group">
            <label for="livestock_quantity">{{ t('tankLog.labels.quantity').value }}</label>
            <input
              type="number"
              id="livestock_quantity"
              v-model="livestockRecord.quantity"
              class="form-control"
              :placeholder="t('tankLog.placeholders.quantity').value"
              step="1"
              min="1"
              max="100"
            >
          </div>
          
          <div class="form-group">
            <label for="livestock_health">{{ t('tankLog.labels.healthStatus').value }}</label>
            <select
              id="livestock_health"
              v-model="livestockRecord.health"
              class="select-control"
            >
              <option value="excellent">{{ t('tankLog.options.healthStatus.excellent').value }}</option>
              <option value="good">{{ t('tankLog.options.healthStatus.good').value }}</option>
              <option value="fair">{{ t('tankLog.options.healthStatus.fair').value }}</option>
              <option value="poor">{{ t('tankLog.options.healthStatus.poor').value }}</option>
              <option value="critical">{{ t('tankLog.options.healthStatus.critical').value }}</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="livestock_behavior">{{ t('tankLog.labels.behaviorObservation').value }}</label>
            <textarea
              id="livestock_behavior"
              v-model="livestockRecord.behavior"
              class="form-control textarea"
              :placeholder="t('tankLog.placeholders.behaviorObservation').value"
              rows="3"
            ></textarea>
          </div>
          
          <div class="form-group">
            <label for="livestock_notes">{{ t('tankLog.labels.notes').value }}</label>
            <textarea
              id="livestock_notes"
              v-model="livestockRecord.notes"
              class="form-control textarea"
              :placeholder="t('tankLog.placeholders.otherNotes').value"
              rows="2"
            ></textarea>
          </div>

          <!-- 生物拍照功能 -->
          <div class="photo-section">
            <h5>{{ t('tankLog.results.photoSection.livestockPhoto').value }}</h5>
            <CameraCapture @photoCaptured="handleLivestockPhotoCaptured" />
            
            <!-- 照片预览 -->
            <div v-if="livestockRecord.photos.length > 0" class="photo-previews">
              <h6>{{ t('tankLog.results.photoSection.photosTaken').value }}</h6>
              <div class="preview-grid">
                <div
                  v-for="(photo, index) in livestockRecord.photos"
                  :key="index"
                  class="photo-preview-item"
                >
                  <img :src="photo" alt="生物照片" class="preview-thumbnail" />
                  <button class="remove-photo-btn" @click="removeLivestockPhoto(index)">
                    ×
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="saveLivestockRecord"
          :disabled="!canSaveLivestockRecord"
        >
          {{ t('tankLog.buttons.saveLivestockRecord').value }}
        </button>
      </div>
      
      <!-- 确认对话框 -->
      <ConfirmDialog
        ref="confirmDialogRef"
        :title="dialogTitle"
        :message="dialogMessage"
        :confirm-text="dialogConfirmText"
        :cancel-text="dialogCancelText"
        @confirm="handleDialogConfirm"
        @cancel="handleDialogCancel"
      />

      <!-- 历史记录 -->
      <div v-if="activeTab === 'history'" class="history-section">
        <div class="history-controls">
          <div class="filter-group">
            <label for="record_type">{{ t('tankLog.labels.recordType').value }}</label>
            <select
              id="record_type"
              v-model="historyFilter.type"
              class="select-control"
            >
              <option value="all">{{ t('tankLog.options.recordType.all').value }}</option>
              <option value="water">{{ t('tankLog.options.recordType.water').value }}</option>
              <option value="maintenance">{{ t('tankLog.options.recordType.maintenance').value }}</option>
              <option value="livestock">{{ t('tankLog.options.recordType.livestock').value }}</option>
            </select>
          </div>
          
          <div class="filter-group">
            <label for="date_range">{{ t('tankLog.labels.dateRange').value }}</label>
            <select
              id="date_range"
              v-model="historyFilter.dateRange"
              class="select-control"
            >
              <option value="7days">{{ t('tankLog.options.dateRange.7days').value }}</option>
              <option value="30days">{{ t('tankLog.options.dateRange.30days').value }}</option>
              <option value="90days">{{ t('tankLog.options.dateRange.90days').value }}</option>
              <option value="all">{{ t('tankLog.options.dateRange.all').value }}</option>
            </select>
          </div>
          
          <button class="filter-btn" @click="clearAllRecords">
            {{ t('tankLog.buttons.clearAllRecords').value }}
          </button>
        </div>
        
        <div class="history-list">
          <div v-if="filteredRecords.length === 0" class="no-records">
            <p>{{ t('tankLog.results.noRecords').value }}</p>
          </div>
          
          <div 
            v-for="record in filteredRecords" 
            :key="record.id" 
            class="history-item"
            :class="getRecordClass(record.type)"
          >
            <div class="record-header">
              <span class="record-type">{{ getRecordTypeLabel(record.type) }}</span>
              <span class="record-date">{{ formatDate(record.timestamp) }}</span>
              <button class="delete-btn" @click="deleteRecord(record.id)">×</button>
            </div>
            
            <div class="record-content">
              <div v-if="record.type === 'water'" class="water-record">
                <div class="param-grid">
                  <div class="param-item">
                    <span class="param-label">{{ t('tankLog.results.historyLabels.temperature').value }}</span>
                    <span class="param-value">{{ record.data.temperature }}°C</span>
                  </div>
                  <div class="param-item">
                    <span class="param-label">{{ t('tankLog.results.historyLabels.salinity').value }}</span>
                    <span class="param-value">
                      {{ record.data.salinity }}
                      <span class="salinity-conversion">({{ t('tankLog.labels.salinityApprox').value }} {{ sgToSalinity(parseFloat(record.data.salinity.toString())) }} ppt)</span>
                    </span>
                  </div>
                  <div class="param-item">
                    <span class="param-label">{{ t('tankLog.results.historyLabels.ph').value }}</span>
                    <span class="param-value">{{ record.data.ph }}</span>
                  </div>
                  <div class="param-item">
                    <span class="param-label">{{ t('tankLog.results.historyLabels.alkalinity').value }}</span>
                    <span class="param-value">{{ record.data.alkalinity }} dKH</span>
                  </div>
                </div>
                <!-- 显示水质照片 -->
                <div v-if="record.data.photos && record.data.photos.length > 0" class="record-photos">
                  <h6>{{ t('tankLog.results.historyLabels.waterPhotos').value }}</h6>
                  <div class="preview-grid">
                    <div
                      v-for="(photo, index) in record.data.photos"
                      :key="index"
                      class="photo-preview-item"
                    >
                      <img :src="photo" alt="水质照片" class="preview-thumbnail" />
                    </div>
                  </div>
                </div>
                <div v-if="record.data.notes" class="record-notes">
                  {{ record.data.notes }}
                </div>
              </div>
              
              <div v-else-if="record.type === 'maintenance'" class="maintenance-record">
                <div class="maintenance-info">
                  <span class="maintenance-type">{{ getMaintenanceTypeLabel(record.data.type) }}</span>
                  <span class="maintenance-duration">{{ record.data.duration }} {{ t('tankLog.results.historyLabels.minutes').value }}</span>
                </div>
                <div v-if="record.data.notes" class="record-notes">
                  {{ record.data.notes }}
                </div>
                <div class="completion-status" :class="{ completed: record.data.completed }">
                  {{ record.data.completed ? t('tankLog.results.completionStatus.completed').value : t('tankLog.results.completionStatus.inProgress').value }}
                </div>
              </div>
              
              <div v-else-if="record.type === 'livestock'" class="livestock-record">
                <div class="livestock-info">
                  <span class="livestock-name">{{ record.data.name }}</span>
                  <span class="livestock-type">{{ getLivestockTypeLabel(record.data.type) }}</span>
                  <span class="livestock-quantity">x{{ record.data.quantity }}</span>
                </div>
                <div class="health-status" :class="record.data.health">
                  {{ t('tankLog.results.historyLabels.healthStatus').value }} {{ getHealthLabel(record.data.health) }}
                </div>
                <!-- 显示生物照片 -->
                <div v-if="record.data.photos && record.data.photos.length > 0" class="record-photos">
                  <h6>{{ t('tankLog.results.historyLabels.livestockPhotos').value }}</h6>
                  <div class="preview-grid">
                    <div
                      v-for="(photo, index) in record.data.photos"
                      :key="index"
                      class="photo-preview-item"
                    >
                      <img :src="photo" alt="生物照片" class="preview-thumbnail" />
                    </div>
                  </div>
                </div>
                <div v-if="record.data.behavior" class="record-notes">
                  {{ record.data.behavior }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import CameraCapture from './CameraCapture.vue'
import ConfirmDialog from './ConfirmDialog.vue'
import { indexedDBService, initIndexedDB, type TankRecord } from '../utils/indexedDB'
import { useI18n } from '../composables/useI18n'

const { t } = useI18n()

// 响应式数据
const activeTab = ref<'water' | 'maintenance' | 'livestock' | 'history'>('water')

// 水质记录数据
const waterParams = ref({
  temperature: 25.5,
  salinity: 1.024,
  ph: 8.2,
  alkalinity: 8.5,
  calcium: 420,
  magnesium: 1350,
  nitrate: 5,
  phosphate: 0.03,
  notes: '',
  photos: [] as string[]
})

// 维护记录数据
const maintenanceRecord = ref({
  type: 'water_change' as 'water_change' | 'filter_cleaning' | 'skimmer_cleaning' | 'glass_cleaning' | 'equipment_check' | 'other',
  waterChangeAmount: 20,
  duration: 30,
  notes: '',
  completed: true
})

// 生物记录数据
const livestockRecord = ref({
  type: 'fish' as 'fish' | 'coral' | 'invertebrate' | 'other',
  name: 'Clownfish',
  quantity: 2,
  health: 'good' as 'excellent' | 'good' | 'fair' | 'poor' | 'critical',
  behavior: 'Active swimming, good appetite',
  notes: '',
  photos: [] as string[]
})

// 历史记录和过滤
const allRecords = ref<TankRecord[]>([])

const historyFilter = ref({
  type: 'all' as 'all' | 'water' | 'maintenance' | 'livestock',
  dateRange: '30days' as '7days' | '30days' | '90days' | 'all'
})

// 计算属性
const canSaveWaterRecord = computed(() => 
  waterParams.value.temperature > 0 && 
  waterParams.value.salinity > 0
)

const canSaveMaintenanceRecord = computed(() => 
  maintenanceRecord.value.duration > 0
)

const canSaveLivestockRecord = computed(() =>
  livestockRecord.value.name && livestockRecord.value.name.trim() !== '' &&
  livestockRecord.value.quantity > 0
)

const filteredRecords = computed(() => {
  const now = Date.now()
  let filtered = allRecords.value
  
  // 按类型过滤
  if (historyFilter.value.type !== 'all') {
    filtered = filtered.filter(record => record.type === historyFilter.value.type)
  }
  
  // 按时间范围过滤
  const rangeMap = {
    '7days': 7 * 24 * 60 * 60 * 1000,
    '30days': 30 * 24 * 60 * 60 * 1000,
    '90days': 90 * 24 * 60 * 60 * 1000
  }
  
  if (historyFilter.value.dateRange !== 'all') {
    const timeRange = rangeMap[historyFilter.value.dateRange]
    filtered = filtered.filter(record => now - record.timestamp <= timeRange)
  }
  
  // 按时间倒序排列
  return filtered.sort((a, b) => b.timestamp - a.timestamp)
})

// 方法
const saveWaterRecord = async () => {
  try {
    const record: TankRecord = {
      id: generateId(),
      type: 'water',
      timestamp: Date.now(),
      data: { ...waterParams.value }
    }
    
    // 保存照片到 IndexedDB
    if (waterParams.value.photos.length > 0) {
      for (const photoData of waterParams.value.photos) {
        await indexedDBService.savePhoto(record.id, photoData)
      }
    }
    
    // 从记录中移除照片数据，因为现在单独存储
    delete record.data.photos
    
    await indexedDBService.saveRecord(record)
    allRecords.value.push(record)
    
    // 重置表单，保留合理的默认值
    Object.assign(waterParams.value, {
      temperature: 25.5,
      salinity: 1.024,
      ph: 8.2,
      alkalinity: 8.5,
      calcium: 420,
      magnesium: 1350,
      nitrate: 5,
      phosphate: 0.03,
      notes: '',
      photos: []
    })
    
    showAlert(t('tankLog.alerts.waterRecordSaved').value, t('tankLog.alerts.success').value)
  } catch (error) {
    console.error('保存水质记录失败:', error)
    showAlert(t('tankLog.alerts.saveFailed').value, t('tankLog.alerts.error').value)
  }
}

const saveMaintenanceRecord = async () => {
  try {
    const record: TankRecord = {
      id: generateId(),
      type: 'maintenance',
      timestamp: Date.now(),
      data: { ...maintenanceRecord.value }
    }
    
    await indexedDBService.saveRecord(record)
    allRecords.value.push(record)
    
    // 重置表单，保留合理的默认值
    Object.assign(maintenanceRecord.value, {
      type: 'water_change',
      waterChangeAmount: 20,
      duration: 30,
      notes: '',
      completed: true
    })
    
    showAlert(t('tankLog.alerts.maintenanceRecordSaved').value, t('tankLog.alerts.success').value)
  } catch (error) {
    console.error('保存维护记录失败:', error)
    showAlert(t('tankLog.alerts.saveFailed').value, t('tankLog.alerts.error').value)
  }
}

const saveLivestockRecord = async () => {
  try {
    const record: TankRecord = {
      id: generateId(),
      type: 'livestock',
      timestamp: Date.now(),
      data: { ...livestockRecord.value }
    }
    
    // 保存照片到 IndexedDB
    if (livestockRecord.value.photos.length > 0) {
      for (const photoData of livestockRecord.value.photos) {
        await indexedDBService.savePhoto(record.id, photoData)
      }
    }
    
    // 从记录中移除照片数据，因为现在单独存储
    delete record.data.photos
    
    await indexedDBService.saveRecord(record)
    allRecords.value.push(record)
    
    // 重置表单，保留合理的默认值
    Object.assign(livestockRecord.value, {
      type: 'fish',
      name: 'Clownfish',
      quantity: 2,
      health: 'good',
      behavior: 'Active swimming, good appetite',
      notes: '',
      photos: []
    })
    
    showAlert(t('tankLog.alerts.livestockRecordSaved').value, t('tankLog.alerts.success').value)
  } catch (error) {
    console.error('保存生物记录失败:', error)
    showAlert(t('tankLog.alerts.saveFailed').value, t('tankLog.alerts.error').value)
  }
}

const deleteRecord = async (id: string) => {
  showConfirmDialog(
    t('tankLog.dialogs.deleteRecord.title').value,
    t('tankLog.dialogs.deleteRecord.message').value,
    t('tankLog.dialogs.deleteRecord.confirmText').value,
    t('dialog.cancel').value,
    async () => {
      try {
        // 删除记录的照片
        await indexedDBService.deletePhotosByRecordId(id)
        // 删除记录
        await indexedDBService.deleteRecord(id)
        allRecords.value = allRecords.value.filter(record => record.id !== id)
        showAlert(t('tankLog.alerts.recordDeleted').value)
      } catch (error) {
        console.error('删除记录失败:', error)
        showAlert(t('tankLog.alerts.saveFailed').value, t('tankLog.alerts.error').value)
      }
    }
  )
}

const clearAllRecords = async () => {
  showConfirmDialog(
    t('tankLog.dialogs.clearAllRecords.title').value,
    t('tankLog.dialogs.clearAllRecords.message').value,
    t('tankLog.dialogs.clearAllRecords.confirmText').value,
    t('dialog.cancel').value,
    async () => {
      try {
        await indexedDBService.clearAllRecords()
        // 清空照片存储
        const transaction = indexedDBService['db']!.transaction(['photos'], 'readwrite')
        const store = transaction.objectStore('photos')
        await new Promise<void>((resolve, reject) => {
          const request = store.clear()
          request.onerror = () => reject(request.error)
          request.onsuccess = () => resolve()
        })
        allRecords.value = []
        showAlert(t('tankLog.alerts.clearAllRecordsSuccess').value)
      } catch (error) {
        console.error('清空记录失败:', error)
        showAlert(t('tankLog.alerts.clearAllRecordsFailed').value, t('tankLog.alerts.error').value)
      }
    }
  )
}

// 照片处理方法
const handleWaterPhotoCaptured = (photoData: string) => {
  waterParams.value.photos.push(photoData)
}

const handleLivestockPhotoCaptured = (photoData: string) => {
  livestockRecord.value.photos.push(photoData)
}

const removeWaterPhoto = (index: number) => {
  waterParams.value.photos.splice(index, 1)
}

const removeLivestockPhoto = (index: number) => {
  livestockRecord.value.photos.splice(index, 1)
}

// 盐度转换函数
const sgToSalinity = (sg: number, temperature: number = 25): number => {
  // 温度补偿
  let correctedSG = sg
  if (temperature !== 25) {
    const tempCorrection = (temperature - 25) * 0.0002
    correctedSG -= tempCorrection
  }
  
  const salinity = ((correctedSG - 1) / 0.766) * 1000
  return Math.round(salinity * 10) / 10
}

// 工具函数
const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2)
}

// 加载记录并关联照片
const loadRecords = async () => {
  try {
    const records = await indexedDBService.getAllRecords()
    
    // 为每个记录加载照片
    for (const record of records) {
      if (record.type === 'water' || record.type === 'livestock') {
        const photos = await indexedDBService.getPhotosByRecordId(record.id)
        record.data.photos = photos
      }
    }
    
    allRecords.value = records
  } catch (error) {
    console.error('加载记录失败:', error)
  }
}

const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp)
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
}

const getRecordClass = (type: string): string => {
  return `record-${type}`
}

const getRecordTypeLabel = (type: string): string => {
  const mapping: Record<string, string> = {
    water: t('tankLog.results.recordType.water').value,
    maintenance: t('tankLog.results.recordType.maintenance').value,
    livestock: t('tankLog.results.recordType.livestock').value
  }
  return mapping[type] || type
}

const getMaintenanceTypeLabel = (type: string): string => {
  const mapping: Record<string, string> = {
    water_change: t('tankLog.options.maintenanceType.waterChange').value,
    filter_cleaning: t('tankLog.options.maintenanceType.filterCleaning').value,
    skimmer_cleaning: t('tankLog.options.maintenanceType.skimmerCleaning').value,
    glass_cleaning: t('tankLog.options.maintenanceType.glassCleaning').value,
    equipment_check: t('tankLog.options.maintenanceType.equipmentCheck').value,
    other: t('tankLog.options.maintenanceType.other').value
  }
  return mapping[type] || type
}

const getLivestockTypeLabel = (type: string): string => {
  const mapping: Record<string, string> = {
    fish: t('tankLog.options.livestockType.fish').value,
    coral: t('tankLog.options.livestockType.coral').value,
    invertebrate: t('tankLog.options.livestockType.invertebrate').value,
    other: t('tankLog.options.livestockType.other').value
  }
  return mapping[type] || type
}

const getHealthLabel = (health: string): string => {
  const mapping: Record<string, string> = {
    excellent: t('tankLog.options.healthStatus.excellent').value,
    good: t('tankLog.options.healthStatus.good').value,
    fair: t('tankLog.options.healthStatus.fair').value,
    poor: t('tankLog.options.healthStatus.poor').value,
    critical: t('tankLog.options.healthStatus.critical').value
  }
  return mapping[health] || health
}

// 对话框相关
const confirmDialogRef = ref<InstanceType<typeof ConfirmDialog> | null>(null)
const dialogTitle = ref('')
const dialogMessage = ref('')
const dialogConfirmText = ref(t('dialog.confirm').value)
const dialogCancelText = ref(t('dialog.cancel').value)
let pendingAction: (() => void) | null = null

// 对话框相关函数
const showConfirmDialog = (
  title: string,
  message: string,
  confirmText: string = '确认',
  cancelText: string = '取消',
  action: () => void
) => {
  dialogTitle.value = title
  dialogMessage.value = message
  dialogConfirmText.value = confirmText
  dialogCancelText.value = cancelText
  pendingAction = action
  confirmDialogRef.value?.show()
}

const showAlert = (message: string, title: string = t('tankLog.dialogs.alert.title').value) => {
  showConfirmDialog(title, message, t('tankLog.dialogs.alert.ok').value, '', () => {})
}

const handleDialogConfirm = () => {
  if (pendingAction) {
    pendingAction()
    pendingAction = null
  }
}

const handleDialogCancel = () => {
  pendingAction = null
}

// 数据库状态
const dbInitialized = ref(false)

onMounted(async () => {
  try {
    await initIndexedDB()
    dbInitialized.value = true
    await loadRecords()
  } catch (error) {
    console.error('数据库初始化失败:', error)
    showAlert(t('tankLog.alerts.dbInitFailed').value, t('tankLog.alerts.error').value)
  }
})
</script>

<style scoped>
/* 组件特定样式 */
.history-section {
  max-height: 600px;
  overflow-y: auto;
}

.history-controls {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 120px;
}

.filter-group label {
  font-size: 0.9em;
  color: #6c757d;
  font-weight: 600;
}

.filter-btn {
  background: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: background-color 0.2s;
}

.filter-btn:hover {
  background: #c82333;
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.no-records {
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-style: italic;
}

.history-item {
  background: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-left: 4px solid #3498db;
}

.record-water {
  border-left-color: #3498db;
}

.record-maintenance {
  border-left-color: #f39c12;
}

.record-livestock {
  border-left-color: #27ae60;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e9ecef;
}

.record-type {
  font-weight: 600;
  color: #2c3e50;
}

.record-date {
  color: #6c757d;
  font-size: 0.9em;
}

.delete-btn {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 1.2em;
  cursor: pointer;
  padding: 0 5px;
  border-radius: 3px;
}

.delete-btn:hover {
  background: #f8d7da;
}

.record-content {
  font-size: 0.95em;
}

.param-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}

.param-item {
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
}

.param-label {
  color: #6c757d;
  font-weight: 500;
}

.param-value {
  color: #2c3e50;
  font-weight: 600;
}

.salinity-conversion {
  font-size: 0.8em;
  color: #6c757d;
  margin-top: 2px;
}

.maintenance-info {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.maintenance-type {
  background: #f39c12;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85em;
}

.maintenance-duration {
  color: #6c757d;
  font-size: 0.9em;
}

.livestock-info {
  display: flex;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.livestock-name {
  font-weight: 600;
  color: #2c3e50;
}

.livestock-type {
  background: #27ae60;
  color: white;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.8em;
}

.livestock-quantity {
  color: #6c757d;
  font-size: 0.9em;
}

.health-status {
  margin-bottom: 8px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: 500;
}

.health-status.excellent {
  background: #d4edda;
  color: #155724;
}

.health-status.good {
  background: #d1ecf1;
  color: #0c5460;
}

.health-status.fair {
  background: #fff3cd;
  color: #856404;
}

.health-status.poor {
  background: #f8d7da;
  color: #721c24;
}

.health-status.critical {
  background: #721c24;
  color: white;
}

.completion-status {
  margin-top: 8px;
  font-size: 0.9em;
  font-weight: 500;
}

.completion-status.completed {
  color: #28a745;
}

.record-notes {
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  color: #495057;
  font-size: 0.9em;
  line-height: 1.4;
}

/* 照片相关样式 */
.photo-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
  overflow: visible; /* 确保相机组件不被裁剪 */
}

/* 历史记录中的照片样式 */
.record-photos {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e9ecef;
}

.record-photos h6 {
  margin-bottom: 10px;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 600;
}

.record-photos .preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
  margin-top: 8px;
}

.record-photos .photo-preview-item {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.record-photos .photo-preview-item:hover {
  transform: scale(1.05);
}

.record-photos .preview-thumbnail {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
}

/* 确保CameraCapture组件在TankLog中正确显示 */
.photo-section .camera-capture {
  margin: 0; /* 重置margin */
  padding: 0; /* 重置padding */
}

.photo-section .camera-preview-container {
  margin-bottom: 15px; /* 调整间距 */
}

.photo-section .camera-interface {
  background: transparent; /* 透明背景 */
  box-shadow: none; /* 移除阴影 */
  padding: 0; /* 移除内边距 */
}

.photo-section h5 {
  margin-bottom: 15px;
  color: #495057;
  font-size: 1.1rem;
}

.photo-section h6 {
  margin-bottom: 10px;
  color: #6c757d;
  font-size: 0.9rem;
}

.photo-previews {
  margin-top: 15px;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-top: 10px;
}

.photo-preview-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.photo-preview-item:hover {
  transform: scale(1.05);
}

.preview-thumbnail {
  width: 100%;
  height: 100px;
  object-fit: cover;
  display: block;
}

.remove-photo-btn {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
}

.remove-photo-btn:hover {
  background: #c82333;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .calculator-tabs{
    margin: 0px 0 15px 0 !important;
  }
  
  .history-controls {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-group {
    min-width: auto;
  }
  
  .param-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .maintenance-info,
  .livestock-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .preview-grid {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
    gap: 8px;
  }
  
  .preview-thumbnail {
    height: 80px;
  }
}
</style>